<template>
    <div class="dateLabel">
        {{ $t('preventif.suivi.modal.equipment.input.label.period') }}
    </div>
    <div class="filter-wrapper">
        <DatePicker
            class="datepicker"
            @setDate="setFilterDateDebut"
            :dateValue="dateDebut"
            :label="$t('start.date')"
        />
        <DatePicker
            class="datepicker"
            @setDate="setFilterDateFin"
            :dateValue="dateFin"
            :label="$t('end.date')"
        />
        <ButtonBlue class="btn" :onClick="filtrerDates" :text="$t('title.filter')" />
    </div>
    <LoaderComponent v-if="isLoading && tableData.length === 0" />
    <div v-else @scroll="handleScroll" ref="content" class="table-container">
        <DataTable
            :tableData="tableData"
            :tableHeader="tableHeader"
            :isLoading="isLoading"
            :count="count"
            :stop-propa-on-pdf="true"
            @sort="doModalSort"
            @select="goToPage"
            @downloadPdf="downloadPdf"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { SortType, sortValue } from '@/models/SortType';
import { EquipementInterventionsActions } from '@/modules/Preventif/store/modules/action-types';
import router from '@/router';
import { InterventionPreventive } from '@/models/InterventionPreventive';
import dayjs from 'dayjs';
import { TableHeader } from '@/models/TableHeader';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import DatePicker from '@/components/ui-components/DatePicker.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { EQUIPEMENT_INTERVENTIONS } from '@/modules/Preventif/store/modules/mutation-types';
import SuiviInterventionService from '@/modules/SuiviInterventions/services/SuiviInterventionService';
import StringUtils from '@/common/utils/StringUtils';

export default defineComponent({
    name: 'HistoriqueEquipement',
    components: {ButtonBlue, DatePicker, LoaderComponent, DataTable },
    props: {
        preventif: { type: Boolean, required: true },
        phaseId: { type: Number, required: false }
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    onMounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.SET_SCROLL, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.nbOfPages) {
                this.fetchData(this.page + 1);
            }
        },
        filtrerDates() {
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.RESET_ITEMS);
            this.fetchData(0);
        },
        setFilterDateFin(value: string) {
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.SET_FILTER_DATE_FIN, value);
        },
        setFilterDateDebut(value: string) {
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.SET_FILTER_DATE_DEBUT, value);
        },
        doModalSort(sort: { sortData: string; sortStatus: SortType }) {
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.SET_SORT, sort);
            this.$store.commit(EQUIPEMENT_INTERVENTIONS.RESET_ITEMS);
            this.fetchData(0);
        },
        fetchData(page: number) {
            if (this.preventif) {
                this.$store.dispatch(EquipementInterventionsActions.GET_EQUIPEMENT_INTERVENTIONS, {
                    page,
                    sort: this.sort,
                });
            } else {
                this.$store.dispatch(EquipementInterventionsActions.GET_ALL_EQUIPEMENT_INTERVENTIONS, {
                    page,
                    sort: this.sort,
                    phaseId: this.phaseId
                });
            }
        },
        goToPage(item: { id: { pageName: string; value: number; }; }) {
            router.push({name: item.id.pageName, params: {id: item.id.value}});
        },
        async downloadPdf(item: { id: { pageName: string; value: number; }; }) {
            const response = await SuiviInterventionService.getRapportPDF(item.id.value);

            if (response.status === 200 && response.data !== undefined) {
                const file = new Blob([response.data], { type: 'application/pdf' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(file);
                link.target = 'blank';
                link.download = 'Rapport_intervention-' + item.id.value;
                link.click();
                URL.revokeObjectURL(link.href);
            }
        }
    },
    computed: {
        tableData(): any {
            if (this.preventif) {
                return this.$store.getters.getEquipementInterventionsItems.map((i: InterventionPreventive) => {
                    return {
                        id: { pageName: 'interventionPreventiveDetailView', value: i.id, label: i.id },
                        typeIntervention: i.typeIntervention,
                        periodicite: StringUtils.getPeriodiciteAsString(i.periodicite),
                        dateDebutPrevue: i.dateDebutPrevue ? dayjs(i.dateDebutPrevue).format('DD/MM/YYYY') : '',
                        dateFinReelle: i.dateFinReelle ? dayjs(i.dateFinReelle).format('DD/MM/YYYY') : '',
                        etat: i.etat ? statusName(i.etat) : '',
                        pdfExist: i.isRapportPresent,
                    };
                });
            } else {
                 if (this.$store.getters.getAllEquipementInterventionsItems != null) {
                    return this.$store.getters.getAllEquipementInterventionsItems.map((i: InterventionPreventive) => {
                        return {
                            id: { pageName: 'interventionPreventiveDetailView', value: i.id, label: i.id },
                            typeIntervention: i.typeIntervention,
                            periodicite: StringUtils.getPeriodiciteAsString(i.periodicite),
                            dateDebutPrevue: i.dateDebutPrevue ? dayjs(i.dateDebutPrevue).format('DD/MM/YYYY') : '',
                            dateFinReelle: i.dateFinReelle ? dayjs(i.dateFinReelle).format('DD/MM/YYYY') : '',
                            etat: i.etat ? statusName(i.etat) : '',
                            pdfExist: i.isRapportPresent
                        };
                    });
                 } else {
                     return [{}];
                 }
            }
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters.getEquipementInterventionsTableHeader;
        },
        isLoading(): boolean {
            return this.$store.getters.getEquipementInterventionsIsLoading;
        },
        count(): number {
            return this.$store.getters.getEquipementInterventionsCount;
        },
        page(): number {
            return this.$store.getters.getEquipementInterventionsPage;
        },
        scroll(): number {
            return this.$store.getters.getEquipementInterventionsScroll;
        },
        nbOfPages(): number {
            return this.$store.getters.getEquipementInterventionsNbOfPages;
        },
        dateDebut(): string {
            return this.$store.getters.getEquipementInterventionFilterDateDebut;
        },
        dateFin(): string {
            return this.$store.getters.getEquipementInterventionFilterDateFin;
        },
        currentEquipmentId(): string {
            return this.$store.getters.getEquipementInterventionsEquipementId;
        },
        sort(): string | undefined {
            return this.$store.getters.getEquipementInterventionsSort !== undefined
                ? sortValue(
                    this.$store.getters.getEquipementInterventionsSort.sortData,
                    this.$store.getters.getEquipementInterventionsSort.sortStatus
                )
                : undefined;
        },
    },
});
</script>

<style scoped>
.dateLabel {
    margin-bottom: 8px;
}
.filter-wrapper {
    display: flex;
}
.filter-wrapper .datepicker {
    width: 200px;
    margin-right: 16px;
}
.table-container {
    margin-top: 16px;
    overflow-y: auto;
}
.btn {
    width: 90px;
    border-radius: 5px;
    cursor: pointer;
}
</style>
